import React, { useState, useMemo } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import BankAccountApiExplorer from '../components/BankAccountApiExplorer';
import { usePartners } from '../context/PartnersContext';
import { useApiMode } from '../context/ApiModeContext';
import './PageStyles.css';
import './BankAccounts.css';


// Mock initial bank accounts matching API response format
const buildInitialBankAccounts = () => [
  {
    id: 2761,
    accountId: 940651,
    isoCountryCode: 'US',
    isoCurrencyCode: 'USD',
    bankName: 'Bank of America',
    beneficiaryName: 'NGLs',
    accountType: 'Checking',
    canRemove: true,
    shortIdentifierDomestic: '5678',
    verificationStatus: 'Unverified',
    status: 'Active',
    externalAccountStatus: 'NotConfigured',
  },
  {
    id: 2762,
    accountId: 940651,
    isoCountryCode: 'US',
    isoCurrencyCode: 'USD',
    bankName: 'Chase',
    beneficiaryName: 'Acme Corp',
    accountType: 'Savings',
    canRemove: true,
    shortIdentifierDomestic: '9012',
    verificationStatus: 'Verified',
    status: 'Active',
    externalAccountStatus: 'Configured',
  },
  {
    id: 2763,
    accountId: 940652,
    isoCountryCode: 'GB',
    isoCurrencyCode: 'GBP',
    bankName: 'Barclays',
    beneficiaryName: 'John Smith',
    accountType: 'Checking',
    canRemove: true,
    shortIdentifierDomestic: '3456',
    verificationStatus: 'Unverified',
    status: 'Active',
    externalAccountStatus: 'NotConfigured',
  },
];

const BankAccounts = () => {
  const { partners, addLogForPartner } = usePartners();
  const { apiBaseUrl } = useApiMode();
  const [bankAccounts] = useState(buildInitialBankAccounts);
  const [filterId, setFilterId] = useState('');
  const [filterBankName, setFilterBankName] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showTryApiSidebar, setShowTryApiSidebar] = useState(false);

  const filteredBankAccounts = useMemo(() => {
    return bankAccounts.filter((b) => {
      if (filterId && String(b.id) !== String(filterId) && !String(b.accountId).includes(filterId)) return false;
      if (filterBankName && !(b.bankName || '').toLowerCase().includes(filterBankName.trim().toLowerCase())) return false;
      if (filterCountry && (b.isoCountryCode || '').toLowerCase() !== filterCountry.trim().toLowerCase()) return false;
      if (filterStatus && (b.status || '').toLowerCase() !== filterStatus.trim().toLowerCase()) return false;
      return true;
    });
  }, [bankAccounts, filterId, filterBankName, filterCountry, filterStatus]);

  return (
    <DashboardLayout>
      <div className="page-container">
        <div className="card">
          <div className="card-header card-header-bank-accounts">
            <h2>Bank Account</h2>
            <div className="bank-accounts-header-actions">
              <button type="button" className="btn btn-try-api" onClick={() => setShowTryApiSidebar(true)}>
                Try API
              </button>
            </div>
          </div>

          <div className="bank-accounts-filters">
            <div className="filter-group">
              <label>ID / Account ID</label>
              <input
                type="text"
                className="form-input filter-input"
                placeholder="Filter by ID"
                value={filterId}
                onChange={(e) => setFilterId(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>Bank name</label>
              <input
                type="text"
                className="form-input filter-input"
                placeholder="Filter by bank name"
                value={filterBankName}
                onChange={(e) => setFilterBankName(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>Country</label>
              <input
                type="text"
                className="form-input filter-input"
                placeholder="e.g. US, GB"
                value={filterCountry}
                onChange={(e) => setFilterCountry(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>Status</label>
              <select
                className="form-input filter-input"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Account ID</th>
                <th>Country</th>
                <th>Currency</th>
                <th>Bank name</th>
                <th>Beneficiary</th>
                <th>Type</th>
                <th>Verification</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBankAccounts.map((account) => (
                <tr key={account.id}>
                  <td><code className="bank-account-id-cell">{account.id}</code></td>
                  <td><code className="bank-account-id-cell">{account.accountId}</code></td>
                  <td>{account.isoCountryCode}</td>
                  <td>{account.isoCurrencyCode}</td>
                  <td>{account.bankName}</td>
                  <td>{account.beneficiaryName}</td>
                  <td>{account.accountType}</td>
                  <td>
                    <span className={`status-badge ${(account.verificationStatus || '').toLowerCase()}`}>
                      {account.verificationStatus}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${(account.status || '').toLowerCase()}`}>
                      {account.status}
                    </span>
                  </td>
                  <td>
                    <button type="button" className="btn btn-success btn-sm">View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {showTryApiSidebar && (
          <div className="try-api-sidebar-overlay" onClick={() => setShowTryApiSidebar(false)} aria-hidden="true" />
        )}
        {showTryApiSidebar && (
          <aside className="try-api-sidebar">
            <BankAccountApiExplorer
              baseUrl={apiBaseUrl}
              partners={partners}
              onClose={() => setShowTryApiSidebar(false)}
              onLogEntry={addLogForPartner}
            />
          </aside>
        )}
      </div>
    </DashboardLayout>
  );
};

export default BankAccounts;
